/**
 * @file DeviceManifestRegistry.ts
 * @description SDK 运行时 manifest 索引。设备形态层可通过 VID/PID 找到应创建的真实设备驱动清单。
 */

import type { DeviceManifest } from '../types/manifest'
import type { NormalizedDeviceManifest } from './ManifestTypes'

const manifestsByIdentity = new Map<string, NormalizedDeviceManifest>()

/** 在 manifest 归一化后建立运行时索引，供底座子设备按 VID/PID 创建真实驱动。 */
export function registerDeviceManifests(manifests: NormalizedDeviceManifest[]): void {
  for (const manifest of manifests) {
    const productIds = Array.isArray(manifest.hardware.productId)
      ? manifest.hardware.productId
      : [manifest.hardware.productId]

    for (const productId of productIds) {
      manifestsByIdentity.set(createIdentityKey(manifest.hardware.vendorId, productId), manifest)
    }
  }
}

/** 查找子设备 manifest 时返回拷贝，避免运行态 currentPid 污染全局注册表。 */
export function findRegisteredManifest(
  vendorId: number,
  productId: number,
): NormalizedDeviceManifest | undefined {
  const manifest = manifestsByIdentity.get(createIdentityKey(vendorId, productId))
  if (!manifest) return undefined
  return cloneManifest(manifest)
}

/** VID/PID 是当前设备身份的最小稳定键。 */
function createIdentityKey(vendorId: number, productId: number): string {
  return `${vendorId}:${productId}`
}

/** manifest 会被驱动写入运行态字段，因此取出时必须隔离引用。 */
function cloneManifest<T extends DeviceManifest>(manifest: T): T {
  return JSON.parse(JSON.stringify(manifest)) as T
}
